import React, { useState } from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import { voteGalleryItem } from '../api/galleryApi';
import useAuth from '../../../features/auth/hooks/useAuth';
import brand from '../../../config/brand';

interface VoteButtonProps {
  itemId: string;
  initialCount: number;
  initialVoted: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const VoteButton: React.FC<VoteButtonProps> = ({
  itemId,
  initialCount,
  initialVoted,
  disabled = false,
  size = 'medium',
}) => {
  const { isAuthenticated } = useAuth();
  const [count, setCount] = useState(initialCount);
  const [voted, setVoted] = useState(initialVoted);
  const [animating, setAnimating] = useState(false);

  const handleVote = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !isAuthenticated) return;

    setAnimating(true);
    setTimeout(() => setAnimating(false), 400);

    // Optimistic update
    const wasVoted = voted;
    setVoted(!voted);
    setCount((prev) => (wasVoted ? prev - 1 : prev + 1));

    try {
      const result = await voteGalleryItem(itemId);
      setCount(result.voteCount);
      setVoted(result.hasVoted);
    } catch {
      // Revert on error
      setVoted(wasVoted);
      setCount(initialCount);
    }
  };

  const iconSize = size === 'large' ? 28 : size === 'medium' ? 22 : 18;

  return (
    <Tooltip
      title={
        !isAuthenticated
          ? 'Sign in to vote'
          : disabled
          ? 'Cannot vote on your own item'
          : voted
          ? 'Remove vote'
          : 'Upvote'
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <IconButton
          onClick={handleVote}
          disabled={disabled || !isAuthenticated}
          size={size}
          sx={{
            color: voted ? brand.colors.fire : 'text.secondary',
            transition: 'all 0.3s ease',
            transform: animating ? 'scale(1.3)' : 'scale(1)',
            '&:hover': {
              color: brand.colors.fire,
              bgcolor: `rgba(${brand.colorsRgb.fire}, 0.08)`,
            },
          }}
        >
          {voted ? (
            <FavoriteIcon sx={{ fontSize: iconSize }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: iconSize }} />
          )}
        </IconButton>
        <Typography
          variant={size === 'large' ? 'body1' : 'body2'}
          fontWeight={700}
          color={voted ? brand.colors.fire : 'text.secondary'}
        >
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default VoteButton;
